import { useEffect, useRef, useState } from 'react';

import { logEvent } from '~/config/firebase.config';
import {
    SCREEN_TAB_CHANNEL,
    dormantURL,
    markLeavingForOtherTab,
    startScreenTabLock,
    type ScreenTabDeps,
    type ScreenTabLock,
} from '~/services/screenTab/screenTabLock';

/** Un par chargement de page : survit aux navigations internes (menu ↔ jeu) du même onglet. */
const TAB_ID = (() => {
    try {
        return crypto.randomUUID();
    } catch {
        return `${Date.now()}-${Math.random()}`;
    }
})();

const bothKey = (clientID: string) => `ub:screenTab:keepBoth:${clientID}`;

function browserDeps(clientID: string): ScreenTabDeps {
    return {
        createChannel: () =>
            typeof BroadcastChannel === 'undefined'
                ? null
                : (new BroadcastChannel(SCREEN_TAB_CHANNEL) as unknown as ReturnType<ScreenTabDeps['createChannel']>),
        isHidden: () => document.visibilityState === 'hidden',
        keepsBoth: (id) => {
            try {
                return sessionStorage.getItem(bothKey(id)) === '1';
            } catch {
                return false;
            }
        },
        rememberBoth: (id) => {
            try {
                sessionStorage.setItem(bothKey(id), '1');
            } catch {
                /* stockage refusé : on redemandera, rien de grave */
            }
        },
        goDormant: () => {
            // Événement GA4 `screen_tab_dormant` : envoyé par la page d'attente (celui d'un
            // onglet caché qui part se perdrait).
            // Sans ça, le « Quitter le site ? » des jeux (useDataCollector) annulerait la veille.
            markLeavingForOtherTab();
            window.location.replace(dormantURL(window.location.pathname + window.location.search, clientID));
        },
    };
}

/**
 * Un seul onglet écran actif par établissement dans ce navigateur (détail et
 * raison : services/screenTab/screenTabLock.ts). `enabled` = mêmes conditions que
 * le suivi de la clé partagée `screenInfos.anim`. Renvoie le nombre d'autres
 * onglets écran du bar sur lesquels demander, et les deux réponses possibles.
 */
export function useScreenTabLock(clientID: string | undefined, enabled: boolean) {
    const [others, setOthers] = useState(0);
    const lockRef = useRef<ScreenTabLock | null>(null);

    useEffect(() => {
        if (!enabled || !clientID) return undefined;
        const lock = startScreenTabLock(clientID, browserDeps(clientID), TAB_ID, setOthers);
        lockRef.current = lock;
        // Onglet fermé ou rechargé : le cleanup React ne passe pas → prévenir les autres.
        const onPageHide = () => lock.stop();
        // Page restaurée par « Précédent » (cache du navigateur) : canal fermé, état
        // figé → on repart d'un chargement neuf plutôt que d'un écran sourd.
        const onPageShow = (e: PageTransitionEvent) => {
            if (e.persisted) window.location.reload();
        };
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('pageshow', onPageShow);
        return () => {
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('pageshow', onPageShow);
            lock.stop();
            lockRef.current = null;
            setOthers(0);
        };
    }, [clientID, enabled]);

    return {
        others,
        takeOver: () => {
            if (clientID) logEvent('screen_tab_take_over', clientID, { clientID, others });
            lockRef.current?.takeOver();
        },
        keepBoth: () => {
            if (clientID) logEvent('screen_tab_keep_both', clientID, { clientID, others });
            lockRef.current?.keepBoth();
        },
    };
}
